/** @jsx jsx */
import { Icon, Table } from "@cd2/components"
import { camelCase, doo, humanize, IComponentProp, titleize } from "@cd2/tools"
import { css, jsx } from "@emotion/core"
import format from "date-fns/format"
import React from "react"
import { Link } from "react-router-dom"
import { icons } from "../../constants/icons"
import { HoverBranchLoader } from "../../hoverLoaders/HoverBranchLoader"
import { HoverCustomerLoader } from "../../hoverLoaders/HoverCustomerLoader"
import { HoverQuoteLoader } from "../../hoverLoaders/HoverQuoteLoader"
import { HoverUserLoader } from "../../hoverLoaders/HoverUserLoader"
import { buildRoute, eRoutes } from "../../routes"
import { IRecord } from "../../types/schema"
import { duplicateOrder } from "../../utils/duplicateOrder"
import { IProductLocationStockResponseItem, useApi } from "../../hooks/useApi"
import { getTotals } from "../../utils/getQuoteOrderTotals"
import { isOpenRma } from "../../utils/isOpenRma"
import { useLocationsStockValues } from "../../utils/useLocationsStockValues"
import { useSettings } from "../../utils/useSettings"
import { formatPrice } from "../../utils/utils"
import { ConsolidatedOrderList } from "../ConsolidatedOrderList"
import { useDownload } from "../DownloadFileButton"
import { brandColors } from "../EOSThemeProvider"
import { CheckboxColumn } from "../FormFields/CheckboxColumn"
import { useGlobalState } from "../GlobalStateProvider"
import { QuickToolTip } from "../QuickTooltip"
import { UserSelectList } from "../SelectLists/UserSelectList"
import { TextWithNewLines } from "../TextWithNewLines"
import {
  BackOrderLink,
  EditedAtColumn,
  ISort,
  LoadingTable,
  MenuColumn,
  NoResultsBlock,
  SageStatusColumn,
  StatusIconsColumn,
} from "./utils"
import { getCurrencyOption } from "../../utils/currency"
import { SubCustomerAsyncSelectField } from "../SelectLists/SubCustomerAsyncSelectField"
import { CustomerTagFilter } from "../FormFields/CustomerTagFilter"
import { ICustomerSelect2 } from "../SelectLists/CustomerSelectList2"
import { subDays } from "date-fns"
import { uppercase } from "../../utils/strings"

OrdersTable.allColumns = [
  "notes" as const,
  "notification" as const,
  "identifier" as const,
  "branch" as const,
  "customer" as const,
  "contact" as const,
  "quote" as const,
  "purchaseOrderReference" as const,
  "consolidatedOrders" as const,
  "deliveryMethod" as const,
  "totalPrice" as const,
  "takenBy" as const,
  "status" as const,
  "sageStatus" as const,
  "date" as const,
  "dateUpdated" as const,
  "dateCreated" as const,
  "archived" as const,
  "actions" as const,
]

interface IProps {
  data: IOrder[] | null
  sort: ISort
  onSortClick: (sort: ISort) => void
  columns?: (typeof OrdersTable.allColumns)[number][]
  selectedIds?: string[]
  refetchConsolidationsKey?: number
  disableSelect?: (row: any) => boolean
  showBarscanInternalId?: boolean

  refetch?(): void

  onSelect?(ids: string[]): void
}

export type IOrder = IRecord<
  "orders",
  | "archived"
  | "delivery_method"
  | "identifier"
  | "pdf_generated"
  | "purchase_order_reference"
  | "processed"
  | "sage_export_status"
  | "sage_order_number"
  | "updated_at"
  | "created_at"
  | "carriage_pennies"
  | "notes"
  | "consolidated_orders_key"
  | "currency_name"
  | "purchaseRequisitionDownloadedAt"
  | "barscanInternalOrderTransferId"
  | "required_date"
  | "type"
  | "paymentMethod"
> & {
  branch?: IRecord<"branches", "name">
  warehouseOverride?: IRecord<"warehouses", "disable_barscan">
  orderRequest?: IRecord<"orderRequests", "purchase_order" | "identifier" | "status">
  purchaseOrders?: (IRecord<"purchase_orders", "identifier"> & {
    order_request: IRecord<"orderRequests", "status">
  })[]
  bookedIn?: boolean
  user: IRecord<"skeem_users", "name"> | null
  customer: IRecord<"customers", "name" | "default_sage_tax_rate_id" | "tax_rate" | "cash_sales">
  contact?: IRecord<"contacts", "name" | "email">
  internal_transfer_to_branch?: IRecord<"branches", "name">
  quote: IRecord<"quotes", "identifier"> | null
  delivery: IRecord<"deliveries"> | null
  childOrder: IRecord<"orders", "identifier"> | null
  parentOrder: IRecord<"orders", "identifier"> | null
  items: (IRecord<
    "order_items",
    "price_pennies" | "quantity" | "discountPercent" | "type" | "allocated_quantity"
  > & {
    product_location: IRecord<"product_locations">
  })[]
  address?: IRecord<"addresses", "line_1" | "postcode">
  billing_address?: IRecord<"addresses", "line_1" | "postcode">
  return_request_source_rma?: IRecord<"rmas", "identifier" | "created_at"> | null
}

export function OrdersTable({
  data,
  sort,
  onSortClick,
  columns = OrdersTable.allColumns,
  refetch,
  onSelect,
  selectedIds,
  refetchConsolidationsKey,
  disableSelect,
  showBarscanInternalId,
}: IProps) {
  const skeem = window.skeem
  const api = useApi()
  const { currentUser } = useGlobalState()
  const { barScanOrders, useExplicitAllocations, useOrderPaymentMethod } = useSettings()
  const download = useDownload()
  const locationIds = Array.from(
    new Set(
      ((data ?? []) as IOrder[]).flatMap(order =>
        order.items.map(item => item.product_location?.id).filter((id): id is string => !!id),
      ),
    ),
  ).sort()
  const locationStock = useLocationsStockValues(locationIds)

  function currentSort(field: ISort["by"]) {
    return JSON.stringify(field) === JSON.stringify(sort.by) ? uppercase(sort.direction) : undefined
  }

  function handleSortClick(field: ISort["by"], defaultSortDirection: ISort["direction"] = "ASC") {
    onSortClick({
      by: field,
      direction:
        JSON.stringify(field) === JSON.stringify(sort.by)
          ? sort.direction === "ASC"
            ? "DESC"
            : "ASC"
          : defaultSortDirection || "ASC",
    })
  }

  if (!data) {
    return <LoadingTable />
  }

  if (data.length === 0) {
    return <NoResultsBlock />
  }

  const columnNodes: Record<(typeof OrdersTable.allColumns)[number], React.ReactNode> = {
    notes: (
      <Table.Column
        css={css`
          padding-right: 0;
        `}
      >
        {row =>
          row.notes ? (
            <QuickToolTip
              content={
                <div
                  css={css`
                    * {
                      color: white;
                    }
                  `}
                >
                  <TextWithNewLines text={row.notes} paragraphProps={{ color: "white" }} />
                </div>
              }
            >
              <Icon icon={icons.notes} color={brandColors.darkMint} />
            </QuickToolTip>
          ) : (
            <Icon icon={icons.notes} color="#f5f5f5" />
          )
        }
      </Table.Column>
    ),
    notification: (
      <Table.Column
        css={css`
          padding-right: 0;
        `}
      >
        {row =>
          useOrderPaymentMethod && row.customer?.cash_sales && !row.paymentMethod ? (
            <QuickToolTip content={"Missing payment information"}>
              <Icon icon={icons.info} color={brandColors.darkMint} />
            </QuickToolTip>
          ) : canFulfillStock(row, locationStock, useExplicitAllocations) ? (
            <QuickToolTip
              content={"There is enough stock available for this order to be fulfilled"}
            >
              <Icon icon={icons.info} color={brandColors.darkMint} />
            </QuickToolTip>
          ) : (
            <Icon icon={icons.info} color="#f5f5f5" />
          )
        }
      </Table.Column>
    ),
    identifier: (
      <Table.Column
        heading="ID"
        sorted={currentSort("identifier")}
        onClick={() => handleSortClick("identifier", "DESC")}
      >
        {row => (
          <React.Fragment>
            {!!row.internal_transfer_to_branch && (
              <React.Fragment>
                <QuickToolTip
                  content={`This order is an internal branch transfer for ${row.internal_transfer_to_branch?.name}`}
                >
                  <span
                    css={css`
                      font-weight: 600;
                      font-size: 12px;
                      color: #666;
                      text-decoration: underline;
                      text-decoration-style: dashed;
                    `}
                  >
                    Internal {row.type === "return_request" ? "Return" : "Transfer"}
                  </span>
                </QuickToolTip>
                <br />
                {showBarscanInternalId && row.barscanInternalOrderTransferId && (
                  <p
                    css={css`
                      font-size: 12px;
                      margin: 0;
                      margin-bottom: 4px;
                      color: #666;
                    `}
                  >
                    Transfer ID: {row.barscanInternalOrderTransferId}
                  </p>
                )}
              </React.Fragment>
            )}
            {doo(() => {
              const missingPayment =
                useOrderPaymentMethod && row.customer?.cash_sales && !row.paymentMethod
              const identifierStyle = missingPayment
                ? css`
                    color: ${orderColorLegend["missing-payment-details"]};
                  `
                : undefined
              return row.branch?.id && row.branch?.id !== currentUser.branch.id ? (
                <div>
                  <b css={identifierStyle}>
                    {row.branch.name}: {row.identifier}
                  </b>
                  <br />
                  via order request{" "}
                  {row.orderRequest?.identifier && (
                    <Link
                      to={buildRoute(eRoutes["/order-requests/:id/preview"], {
                        id: row.orderRequest.id,
                      })}
                    >
                      {row.orderRequest.identifier}
                    </Link>
                  )}
                </div>
              ) : (
                <div>
                  {row.type === "return_request" && (
                    <div>
                      <b>RMA{row.return_request_source_rma?.identifier}</b>
                    </div>
                  )}
                  <Link to={buildRoute(eRoutes["/orders/:id/preview"], { id: row.id })}>
                    <b css={identifierStyle}>{row.identifier}</b>
                  </Link>
                </div>
              )
            })}
            <BackOrderLink order={row} />
          </React.Fragment>
        )}
      </Table.Column>
    ),
    branch: (
      <Table.DataColumn
        field="branch"
        heading="Branch"
        sorted={currentSort("branch")}
        onClick={() => handleSortClick("branch")}
        render={branch =>
          branch ? <HoverBranchLoader id={branch.id} initialData={branch} /> : null
        }
      />
    ),
    customer: (
      <Table.Column
        heading="Customer"
        sorted={currentSort("customer")}
        onClick={() => handleSortClick("customer")}
      >
        {row => (
          <HoverCustomerLoader
            id={row.customer?.id}
            initialData={row.customer}
            renderInlineLink="block"
          />
        )}
      </Table.Column>
    ),
    quote: (
      <Table.Column
        heading="Quote"
        sorted={currentSort("quote")}
        onClick={() => handleSortClick("quote")}
      >
        {row =>
          row.quote ? (
            <HoverQuoteLoader id={row.quote.id} initialData={row.quote} renderInlineLink="block" />
          ) : null
        }
      </Table.Column>
    ),
    purchaseOrderReference: (
      <Table.Column
        heading="Reference"
        sorted={currentSort("purchase_order_reference")}
        onClick={() => handleSortClick("purchase_order_reference")}
      >
        {row => row.purchase_order_reference}
      </Table.Column>
    ),
    deliveryMethod: (
      <Table.Column
        heading="Del. Method"
        sorted={currentSort("delivery_method")}
        onClick={() => handleSortClick("delivery_method")}
      >
        {row => row.delivery_method}
      </Table.Column>
    ),
    totalPrice: (
      <Table.Column heading="Total Price">
        {row => {
          const order = row as IOrder
          const currency = getCurrencyOption(
            order.currency_name || currentUser.branch.currency_name,
          )
          const { totalPricePennies } = getTotals(
            order.items,
            order.carriage_pennies,
            order.customer ? order.customer.tax_rate : currency?.taxRate,
          )

          return formatPrice(totalPricePennies, {
            isPennies: true,
            currency: currency?.symbol ?? "",
          })
        }}
      </Table.Column>
    ),
    takenBy: (
      <Table.Column
        heading="Taken by"
        sorted={currentSort("takenBy")}
        onClick={() => handleSortClick("takenBy")}
      >
        {row =>
          row.user ? (
            <HoverUserLoader id={row.user.id} renderInlineLink="block" initialData={row.user} />
          ) : null
        }
      </Table.Column>
    ),
    date: (
      <EditedAtColumn
        heading="Date"
        sorted={currentSort("updated_at")}
        onClick={() => handleSortClick("updated_at", "DESC")}
        field={{ update: "updated_at", create: "created_at", display: "created_at" }}
      />
    ),
    dateUpdated: (
      <Table.DateColumn
        heading="Last updated"
        field="updated_at"
        onSortClick={() => handleSortClick("updated_at")}
        sort={
          currentSort("updated_at")
            ? { by: "updated_at", direction: currentSort("updated_at")! }
            : undefined
        }
        format="dd/MM/yy - p"
      />
    ),
    dateCreated: (
      <Table.DateColumn
        heading="Date created"
        field="created_at"
        onSortClick={() => handleSortClick("created_at")}
        sort={
          currentSort("created_at")
            ? { by: "created_at", direction: currentSort("created_at")! }
            : undefined
        }
        format="dd/MM/yy - p"
      />
    ),
    status: (
      <StatusIconsColumn
        items={row =>
          [
            (!barScanOrders || row.warehouseOverride?.disable_barscan) && {
              icon: icons.print,
              isActive: row.pdf_generated,
              positiveToolTip: "Printed",
              negativeToolTip: "Not printed",
            },
            {
              icon: icons.deliveries,
              isActive: !!row.delivery,
              link: row.delivery
                ? buildRoute(eRoutes["/deliveries/:id/preview"], { id: row.delivery.id })
                : undefined,
              positiveToolTip: "Delivered",
              negativeToolTip: "Not delivered",
            },
            {
              icon: icons.mail,
              isActive: row.acknowledgementEmailSentAt,
              positiveToolTip: row.acknowledgementEmailSentAt
                ? `Email sent to drafts at: ${format(
                    new Date(row.acknowledgementEmailSentAt),
                    "dd/MM/yy - p",
                  )}`
                : "Email sent to drafts",
              negativeToolTip: "No email sent",
            },
            {
              icon: icons.purchaseRequisition,
              isActive: row.purchaseRequisitionDownloadedAt,
              positiveToolTip: row.purchaseRequisitionDownloadedAt
                ? `Purchase Requisition was first downloaded at: ${format(
                    new Date(row.purchaseRequisitionDownloadedAt),
                    "dd/MM/yy - p",
                  )}`
                : "",
              negativeToolTip: "Purchase Requisition not downloaded",
            },
          ].filter(Boolean)
        }
      />
    ),
    sageStatus: (
      <SageStatusColumn
        sorted={currentSort("sage_order_number")}
        onClick={() => handleSortClick("sage_order_number")}
      />
    ),
    contact: (
      <Table.Column heading="Contact">
        {row =>
          row.contact ? (
            <span>
              {row.contact?.name}
              <br />
              {row.contact?.email}
            </span>
          ) : null
        }
      </Table.Column>
    ),
    archived: (
      <Table.BooleanColumn
        heading="Archived"
        field="archived"
        renderTrue={() => <Icon icon={icons.check} />}
      />
    ),
    consolidatedOrders: (
      <Table.DataColumn
        heading="Consolidated Orders"
        field="consolidated_orders_key"
        render={key => (
          <ConsolidatedOrderList refetchKey={refetchConsolidationsKey} consolidatedOrderKey={key} />
        )}
      />
    ),
    actions: (
      <MenuColumn
        items={order =>
          [
            { children: "View", to: buildRoute(eRoutes["/orders/:id/preview"], { id: order.id }) },
            {
              children: "Edit",
              to: buildRoute(eRoutes["/orders/:id/edit"], { id: order.id }),
              disabled: !!order.delivery,
            },
            {
              children: "Duplicate",
              onClick: async () => {
                await duplicateOrder(
                  skeem,
                  api,
                  currentUser as any,
                  order.id,
                  useExplicitAllocations,
                )
                refetch?.()
              },
            },
            !!(currentUser.isAdmin || currentUser.isSuperAdmin || currentUser.canArchiveOrders) && {
              children: order.archived ? "Un-archive" : "Archive",
              onClick: async () => {
                await skeem.mutate2({
                  orders: { update: { id: order.id, archived: !order.archived } },
                })
                refetch?.()
              },
            },
            (!barScanOrders || order.warehouseOverride?.disable_barscan) && {
              children: "Download PDF",
              onClick: () => download(`/pdfs/picking-ticket/${order.id}`),
            },
          ].filter(Boolean)
        }
      />
    ),
  }

  return (
    <div
      css={css`
        tr.archived {
          background: ${orderColorLegend["archived"]};
        }

        tr.canceled-or {
          background: ${orderColorLegend["canceled-or"]};
        }

        tr.is-overdue {
          background: ${orderColorLegend["is-overdue"]};
        }

        tr.new_rma {
          background: ${orderColorLegend["new-rma"]};
        }

        tr.has-linked-source {
          background: ${orderColorLegend["has-linked-source"]};
        }

        tr.is-back-order {
          background: ${orderColorLegend["is-back-order"]};
        }

        tr.booked-in {
          background: ${orderColorLegend["booked-in"]};
        }

        tr.stock-ready {
          background: ${orderColorLegend["stock-ready"]};
        }

        ${disableSelect ? `th:has(input[type="checkbox"]) { display: none !important; }` : ""}
      `}
    >
      <Table
        data={data}
        keyExtractor={row => row.id}
        rowClass={row => getOrderRowClass(row as IOrder, locationStock, useExplicitAllocations)}
      >
        {onSelect && (
          <CheckboxColumn
            field="id"
            onChange={onSelect}
            values={selectedIds ?? []}
            rowDisabled={disableSelect}
          />
        )}
        {columns.map(name => (
          <React.Fragment key={name}>{columnNodes[name]}</React.Fragment>
        ))}
      </Table>
    </div>
  )
}

export const orderColorLegend = {
  "canceled-or": "rgba(239, 68, 68, 0.45)",
  "is-overdue": "rgba(255, 0, 0, 0.1)",
  "new-rma": "rgba(56, 189, 248, 0.32)",
  "missing-payment-details": "#dc2626",
  "has-linked-source": "rgba(128, 0, 128, 0.1)",
  "is-back-order": "rgba(37, 99, 235, 0.16)",
  "booked-in": "rgba(0, 255, 13, 0.334)",
  "stock-ready": "rgba(0, 255, 76, 0.069)",
  archived: "#fffbd2",
}

export type LegendKey = keyof typeof orderColorLegend

/** Row colour priority — highest first; keep in sync with getOrderRowClass. */
export const orderColorLegendKeys: LegendKey[] = [
  "archived",
  "canceled-or",
  "is-overdue",
  "new-rma",
  "missing-payment-details",
  "has-linked-source",
  "is-back-order",
  "booked-in",
  "stock-ready",
]

export function isOrderColorFilterActive(filter: IOrderFilters, key: LegendKey) {
  if (key === "archived") {
    return !!filter.archived
  }
  if (key === "canceled-or") {
    return !!filter.canceledOR
  }
  if (key === "is-overdue") {
    return !!filter.isOverdue
  }
  if (key === "new-rma") {
    return !!filter.rmaOnly
  }
  if (key === "has-linked-source") {
    return !!filter.hasPurchaseOrder
  }
  if (key === "booked-in") {
    return filter.status === "booked"
  }
  if (key === "stock-ready") {
    return filter.status === "ready"
  }
  return false
}

export function toggleOrderColorFilter(
  filter: IOrderFilters,
  key: LegendKey,
): Partial<IOrderFilters> | null {
  if (key === "archived") {
    return { archived: !filter.archived }
  }
  if (key === "canceled-or") {
    return { canceledOR: !filter.canceledOR }
  }
  if (key === "is-overdue") {
    return { isOverdue: !filter.isOverdue }
  }
  if (key === "new-rma") {
    return { rmaOnly: !filter.rmaOnly }
  }
  if (key === "has-linked-source") {
    return { hasPurchaseOrder: !filter.hasPurchaseOrder }
  }
  if (key === "booked-in") {
    return { status: filter.status === "booked" ? undefined : "booked" }
  }
  if (key === "stock-ready") {
    return { status: filter.status === "ready" ? undefined : "ready" }
  }
  return null
}

export function OrderColorLegend({
  keys,
  filter,
  onToggle,
}: {
  keys?: LegendKey[]
  filter?: IOrderFilters
  onToggle?: (key: LegendKey) => void
}) {
  const keysToShow = keys
    ? orderColorLegendKeys.filter(key => keys.includes(key))
    : orderColorLegendKeys
  const items = keysToShow.map(key => ({
    key,
    label: humanize(camelCase(key, true)),
    active: filter ? isOrderColorFilterActive(filter, key) : false,
    canToggle: !!onToggle && toggleOrderColorFilter(filter ?? ({} as IOrderFilters), key) !== null,
  }))
  return (
    <div
      role="list"
      aria-label="Order row colours"
      css={css`
        display: flex;
        flex-wrap: wrap;
        gap: 12px 16px;
        font-size: 12px;
        color: ${brandColors.lightNight};
      `}
    >
      {items.map(({ key, label, active, canToggle }) => (
        <button
          key={key}
          type="button"
          role="listitem"
          aria-pressed={active}
          onClick={canToggle ? () => onToggle?.(key) : undefined}
          disabled={!canToggle}
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0;
            padding: 2px 4px;
            border: 1px solid ${active ? brandColors.darkMint : "transparent"};
            border-radius: 4px;
            background: ${active ? "rgba(0, 188, 168, 0.08)" : "transparent"};
            color: inherit;
            font: inherit;
            cursor: ${canToggle ? "pointer" : "default"};
            ${canToggle
              ? `
              &:hover {
                background: rgba(0, 0, 0, 0.04);
              }
            `
              : ""}
          `}
        >
          <span
            css={css`
              width: 14px;
              height: 14px;
              border-radius: 2px;
              border: 1px solid rgba(0, 0, 0, 0.12);
              background: ${orderColorLegend[key]};
              flex-shrink: 0;
            `}
          />
          <span>{label}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * The "Ready" status filter for branches that do not use explicit allocations: every product line is covered
 * by the location's free stock. Runs in the database, so it reads `free_stock` directly. Branches using
 * explicit allocations cannot do this (free stock minus the other orders' allocations is not a column), so
 * they filter by the ids from `api.readyOrderIds` instead.
 */
export function canFullfillStatus() {
  const coveredByFreeStock = {
    lte: [{ attr: "quantity" }, { path: ["product_location", "free_stock"] }],
  }
  return {
    eq: [
      {
        count: {
          attr: {
            name: "items",
            filter: {
              exists: {
                attr: "product_location",
              },
            },
          },
        },
      },
      {
        count: {
          attr: {
            name: "items",
            filter: coveredByFreeStock,
          },
        },
      },
    ],
  }
}

function getOrderRowClass(
  row: IOrder,
  locationStock: Record<string, IProductLocationStockResponseItem>,
  useExplicitAllocations: boolean,
) {
  // Priority 1 — Archived (yellow)
  if (row.archived) {
    return "archived"
  }
  // Priority 2 — Cancelled linked order request (red)
  if (
    row.orderRequest?.status === "cancelled" ||
    row.purchaseOrders?.some(po => po.order_request?.status === "cancelled")
  ) {
    return "canceled-or"
  }
  // Priority 3 — Is overdue (red), once a full day has passed since the required date
  if (row.required_date != null && new Date(row.required_date) < subDays(new Date(), 1)) {
    return "is-overdue"
  }
  // Not in original spec — RMA not yet booked in (light blue)
  if (isOpenRma(row)) {
    return "new_rma"
  }
  // Missing payment details — identifier is coloured red instead of the row

  const hasLinkedSource =
    (row.purchaseOrders && row.purchaseOrders.length > 0) ||
    !!row.orderRequest ||
    !!row.internal_transfer_to_branch

  // Priority 4 — Linked PO / order request / transfer, not booked in (purple)
  if (hasLinkedSource && !row.bookedIn) {
    return "has-linked-source"
  }
  // Priority 5 — Is back order (blue)
  if (row.childOrder) {
    return "is-back-order"
  }
  // Priority 6 — Linked PO / order request / transfer, booked in (bright green)
  if (hasLinkedSource && row.bookedIn) {
    return "booked-in"
  }
  // Priority 7 — Stock ready (pale green)
  if (canFulfillStock(row, locationStock, useExplicitAllocations)) {
    return "stock-ready"
  }
  // No status yet (white)
  return ""
}

function canFulfillStock(
  row: IOrder,
  locationStock: Record<string, IProductLocationStockResponseItem>,
  useExplicitAllocations: boolean,
) {
  const productItems = row.items.filter(item => item.product_location)
  return productItems.every(item => {
    const stock = locationStock[item.product_location.id]
    if (!stock) {
      return false
    }
    if (useExplicitAllocations) {
      // The line already holds its allocation, so only the rest has to fit in the unallocated stock
      const stillNeeded = Math.max((item.quantity ?? 0) - (item.allocated_quantity ?? 0), 0)
      return stillNeeded <= Math.max(stock.inStock - stock.explicitlyAllocatedStock, 0)
    }
    return (item.quantity ?? 0) <= stock.inStock
  })
}

export const orderStatusFilters = [
  { label: "All booked in", value: "booked" },
  { label: "Ready", value: "ready" },
  { label: "Failed Export", value: "failed_export" },
  { label: "Is Call Off", value: "is_call_off_order" },
] as const

export interface IOrderFilters {
  query: string
  productQuery: string
  sort: { by: any; direction: "ASC" | "DESC" }
  pagination: { page: number; perPage: number }
  archived?: boolean
  canceledOR?: boolean
  customer?: ICustomerSelect2
  subCustomer?: IComponentProp<typeof SubCustomerAsyncSelectField, "value">
  user?: IComponentProp<typeof UserSelectList, "value">
  salesRep?: string
  contact?: string
  internalOnly?: boolean
  hideBackOrders?: boolean
  status?: (typeof orderStatusFilters)[number]["value"]
  tag?: IComponentProp<typeof CustomerTagFilter, "tag">
  tagValue?: IComponentProp<typeof CustomerTagFilter, "value">
  hasPurchaseOrder?: boolean
  isOverdue?: boolean
  rmaOnly?: boolean
}

// TODO: this type is an any as the table on the customer page is wierd - when that gets fixed, can make this better
export function skeemSortForOrdersTable(filter: any) {
  let sort = { ...filter.sort }
  if (sort.by === "recommended") {
    sort = [
      {
        by: "created_at",
        direction: "desc",
      },
    ]
  }
  if (sort.by === "branch") {
    sort.by = { path: ["branch", "name"] }
  }
  if (sort.by === "customer") {
    sort.by = { path: ["customer", "name"] }
  }
  if (sort.by === "quote") {
    sort.by = { path: ["quote", "identifier"] }
  }
  if (sort.by === "takenBy") {
    sort.by = { path: ["user", "name"] }
  }
  return sort
}
